import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react'
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Center,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  Tooltip,
  useDisclosure,
  useToast
} from '@chakra-ui/react'
import {
  CellClassParams,
  CellValueChangedEvent,
  ColDef,
  ICellEditorParams,
  ICellRendererParams,
  RowClickedEvent
} from 'ag-grid-community'
import {
  ChevronDownIcon,
  CheckIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUturnLeftIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/20/solid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  AgentFeedbackRow,
  CreateAgentFeedbackPayload,
  CreateDispositionAgentPayload,
  CreateDispositionBidPayload,
  CreateDispositionPayload,
  CreateListPricePayload,
  DispositionAgent,
  DispositionBidRow,
  DispositionDetailsResponse,
  DispositionSummaryRow,
  DispositionSummaryScope,
  DispositionsTrackerSummaryResponse,
  ListPriceHistoryRow
} from '@homevest/types/dispositions'
import { CAPABILITY_TYPES } from '@homevest/utils/capabilities'
import { DISPOSITION_SALE_TYPES, DISPOSITION_STATUSES } from '@homevest/utils/dispositions'
import { useSelector } from 'react-redux'

import axios from 'lib/axios'
import { hasCapability } from 'lib/admin-perms'
import BasicPageLayout from 'components/TailwindUIToolkit/Layouts/BasicPageLayout'
import { BaseGrid } from 'components/TailwindUIToolkit'
import SetFilter from 'components/AgGrid/filters/SetFilter'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import { StoreState } from 'store'

import {
  badgeSelectCellEditor,
  BooleanCellEditor,
  SearchableCellEditor,
  SearchableFormSelect,
  FormOption
} from './select-editors'

type PatchUpdate = {
  id: string
  [key: string]: unknown
}

type PendingDetailsNavigation = {
  disposition: DispositionSummaryRow | null
}

type FormValues = Record<string, string>

type FormField = {
  name: string
  label: string
  type: 'text' | 'number' | 'date' | 'select' | 'textarea'
  options?: FormOption[]
  searchable?: boolean
  placeholder?: string
  required?: boolean
  min?: number
  max?: number
  step?: number
  showWhen?: (values: FormValues) => boolean
}

const getAgentPayload = (values: FormValues): CreateDispositionBidPayload['agent'] =>
  values.agent_id === 'other'
    ? {
        id: 'other',
        name: values.agent_name,
        brokerage: values.agent_brokerage || null,
        phone: values.agent_phone || null
      }
    : { id: values.agent_id }

const agentFormFields = (options: FormOption[]): FormField[] => [
  {
    name: 'agent_id',
    label: 'Agent',
    type: 'select',
    searchable: true,
    placeholder: 'Search agents...',
    required: true,
    options
  },
  {
    name: 'agent_name',
    label: 'New Agent Name',
    type: 'text',
    required: true,
    showWhen: (values) => values.agent_id === 'other'
  },
  {
    name: 'agent_brokerage',
    label: 'New Agent Brokerage',
    type: 'text',
    showWhen: (values) => values.agent_id === 'other'
  },
  {
    name: 'agent_phone',
    label: 'New Agent Phone',
    type: 'text',
    showWhen: (values) => values.agent_id === 'other'
  }
]

const API_ROOT = 'admin/dispositions-tracker'
const DISPOSITIONS_PAGE_SIZE = 10
const DISPOSITIONS_ROW_HEIGHT = 72
// Reserve space for the header, pagination controls, and horizontal scrollbar.
const DISPOSITIONS_GRID_HEIGHT = 128 + DISPOSITIONS_PAGE_SIZE * DISPOSITIONS_ROW_HEIGHT
const EDITABLE_DATE_FIELDS = new Set([
  'date',
  'bid_received_date',
  'bind_date',
  'due_diligence_end_date',
  'estimated_closing_date',
  'closing_date',
  'terminated_date',
  'showing_date'
])
const EDITABLE_DISPOSITION_STATUS_ORDER = [
  DISPOSITION_STATUSES.PENDING_MOVE_OUT,
  DISPOSITION_STATUSES.PENDING_TURN,
  DISPOSITION_STATUSES.READY_TO_LIST,
  DISPOSITION_STATUSES.LISTING_PREP,
  DISPOSITION_STATUSES.ACTIVE_ON_MLS,
  DISPOSITION_STATUSES.PRE_CONTRACT_AGREEMENT,
  DISPOSITION_STATUSES.PENDING_DUE_DILIGENCE,
  DISPOSITION_STATUSES.CLEAR_TO_CLOSE
]
const EDITABLE_DISPOSITION_STATUS_RANK = new Map<string, number>(
  EDITABLE_DISPOSITION_STATUS_ORDER.map((status, index) => [status, index])
)
const DISPOSITION_STATUS_MINOR_WORDS = new Set(['on', 'to'])

const formatDispositionStatusLabel = (status: string) =>
  status
    .split('_')
    .map((word, index) => {
      if (word === 'mls') {
        return 'MLS'
      }

      if (index > 0 && DISPOSITION_STATUS_MINOR_WORDS.has(word)) {
        return word
      }

      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')

const EMPTY_SUMMARY: DispositionsTrackerSummaryResponse = {
  dispositions: [],
  editableStatuses: [],
  homesNotInDisposition: [],
  agents: [],
  counterparties: [],
  counterpartyTypes: []
}

const EMPTY_DETAILS: DispositionDetailsResponse = {
  listPrices: [],
  bids: [],
  feedback: []
}

const saleTypeOptions = Object.values(DISPOSITION_SALE_TYPES).map((value) => ({
  label: value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' '),
  value
}))

const AGENT_FEEDBACK_STATUS_OPTIONS: FormOption[] = [
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' }
]

const AGENT_FEEDBACK_INTEREST_OPTIONS: FormOption[] = [
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'somewhat', label: 'Somewhat' },
  { value: 'yes', label: 'Yes' },
  { value: 'very', label: 'Very' }
]

const AGENT_FEEDBACK_EXPERIENCE_OPTIONS: FormOption[] = [
  { value: 'excellent', label: 'Excellent' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' }
]

const AGENT_FEEDBACK_PRICE_OPINION_OPTIONS: FormOption[] = [
  { value: 'too_high', label: 'Too High' },
  { value: 'good', label: 'Good' },
  { value: 'just_right', label: 'Just Right' },
  { value: 'too_low', label: 'Too Low' }
]

const formatMoney = (value: unknown) => {
  if (value == null || value === '') {
    return ''
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return String(value)
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(parsed)
}

const formatDate = (value: unknown) => {
  if (!value) {
    return ''
  }

  const date = new Date(value as string)
  if (Number.isNaN(date.getTime())) {
    return String(value)
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(date)
}

const toInputDate = (value: unknown) => {
  if (!value) {
    return ''
  }

  const date = new Date(value as string)
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  return date.toISOString().slice(0, 10)
}

const toLocalInputDate = (date: Date) => {
  if (Number.isNaN(date.getTime())) {
    return ''
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const DateCellEditor = forwardRef(
  ({ value, node, column }: ICellEditorParams, ref: React.Ref<{ getValue: () => string }>) => {
    const initialDate = toInputDate(value)
    const [date, setDate] = useState(initialDate)
    const dateRef = useRef(initialDate)
    const inputRef = useRef<HTMLInputElement>(null)
    const handleDateChange = (
      event: React.ChangeEvent<HTMLInputElement> | React.FormEvent<HTMLInputElement>
    ) => {
      const nextDate = event.currentTarget.value
      if (nextDate === dateRef.current) {
        return
      }
      dateRef.current = nextDate
      setDate(nextDate)
      // Stage the draft immediately without ending native date-segment keyboard entry.
      node.setDataValue(column, nextDate)
    }

    useImperativeHandle(ref, () => ({
      getValue: () => dateRef.current
    }))

    useEffect(() => {
      inputRef.current?.focus()
    }, [])

    return (
      <Input
        ref={inputRef}
        type='date'
        size='sm'
        value={date}
        onChange={handleDateChange}
        onInput={handleDateChange}
        onKeyDownCapture={(event) => {
          if (event.key === 'Escape') {
            dateRef.current = initialDate
            setDate(initialDate)
            node.setDataValue(column, initialDate)
          }
        }}
      />
    )
  }
)
DateCellEditor.displayName = 'DateCellEditor'

const RatingCellEditor = forwardRef(
  ({ value }: ICellEditorParams, ref: React.ForwardedRef<{ getValue: () => number | string }>) => {
    const initialValue = value == null ? '' : String(value)
    const [rating, setRating] = useState(initialValue)
    const ratingRef = useRef(initialValue)
    const inputRef = useRef<HTMLInputElement>(null)

    useImperativeHandle(ref, () => ({
      getValue: () => (ratingRef.current ? Number(ratingRef.current) : '')
    }))

    useEffect(() => {
      inputRef.current?.select()
    }, [])

    return (
      <Input
        ref={inputRef}
        aria-label='Rating from 1 to 5'
        type='number'
        min={1}
        max={5}
        step={1}
        size='sm'
        value={rating}
        onChange={(event) => {
          const nextRating = event.target.value
          if (nextRating === '' || /^[1-5]$/.test(nextRating)) {
            ratingRef.current = nextRating
            setRating(nextRating)
          }
        }}
      />
    )
  }
)
RatingCellEditor.displayName = 'RatingCellEditor'

const yesNoFormatter = ({ value }: { value: unknown }) => {
  if (value === true || value === 'true') {
    return 'Yes'
  }

  if (value === false || value === 'false') {
    return 'No'
  }

  return ''
}

const valueFormatter = (formatter: (value: unknown) => string) => (params: { value: unknown }) =>
  formatter(params.value)

const badgeCellRenderer = (params: ICellRendererParams) =>
  params.value ? mapContentToBadge(params.value, { dot: false, size: 'xs' }) : ''

const getColumnLabel = <T extends object>(column: ColDef<T>) =>
  String(column.headerName ?? column.field ?? 'Column')

const markEditableColumn = <T extends object>(column: ColDef<T>): ColDef<T> => {
  const label = getColumnLabel(column)

  return {
    ...column,
    editable: column.editable ?? true,
    headerName: `${label} ✎`,
    headerTooltip: column.headerTooltip ?? `${label} is editable. Click a cell to edit.`,
    tooltipValueGetter: column.tooltipValueGetter ?? (() => `Click to edit ${label}.`)
  }
}

const markDetailsManagedColumn = <T extends object>(
  column: ColDef<T>,
  tooltip: string
): ColDef<T> => ({
  ...column,
  headerTooltip: column.headerTooltip ?? tooltip,
  tooltipValueGetter: column.tooltipValueGetter ?? (() => tooltip)
})

const markEditableDateColumn = <T extends object>(column: ColDef<T>): ColDef<T> =>
  markEditableColumn({
    ...column,
    cellEditor: DateCellEditor,
    valueFormatter: column.valueFormatter ?? valueFormatter(formatDate)
  })

const isDateColumn = <T extends object>(column: ColDef<T>) =>
  column.field ? EDITABLE_DATE_FIELDS.has(column.field) : false

const getComparableCellValue = <T extends object>(column: ColDef<T>, value: unknown) => {
  const comparableValue = isDateColumn(column) ? toInputDate(value) : value
  return comparableValue == null ? '' : comparableValue
}

const getErrorMessage = (error: any) =>
  error?.response?.data?.message ??
  error?.response?.data?.error ??
  error?.message ??
  error?.error ??
  'Request failed. Check the API and retry.'

const showToastError = (toast: ReturnType<typeof useToast>, title: string, error: any) => {
  toast({
    title,
    description: getErrorMessage(error),
    status: 'error',
    duration: 7000,
    isClosable: true
  })
}

const getDetailsGridHeight = (rowCount: number, maxHeight: number) =>
  Math.min(maxHeight, Math.max(150, 92 + rowCount * 44))

const getDispositionRowId = (row: DispositionSummaryRow) => row.disposition_id
const getEntityRowId = (row: { id: string }) => row.id

const EditableGrid = <T extends object>({
  rows,
  columns,
  getRowId,
  onChange,
  onRowClicked,
  pendingUpdates = {},
  resetKey = 0,
  height = 420,
  rowAutoHeight = false,
  paginated = false,
  searchText = '',
  isEditingDisabled = false
}: {
  rows: T[]
  columns: ColDef<T>[]
  getRowId: (row: T) => string
  onChange?: (id: string, field: string, value: unknown, isOriginalValue: boolean) => void
  onRowClicked?: (row: T) => void
  pendingUpdates?: Record<string, PatchUpdate>
  resetKey?: number
  height?: number
  rowAutoHeight?: boolean
  paginated?: boolean
  searchText?: string
  isEditingDisabled?: boolean
}) => {
  const draftRows = useMemo(
    () => rows.map((row) => ({ ...row, ...pendingUpdates[getRowId(row)] })),
    [getRowId, pendingUpdates, resetKey, rows]
  )
  const [gridContainer, setGridContainer] = useState<HTMLDivElement | null>(null)
  const popupParent =
    (gridContainer?.closest('[role="dialog"]') as HTMLElement | null) ??
    (typeof document === 'undefined' ? undefined : document.body)
  const columnsWithPendingEdits = useMemo(
    () =>
      columns.map((column) => ({
        ...column,
        editable: isEditingDisabled ? false : column.editable,
        cellClassRules: {
          ...column.cellClassRules,
          'dispositions-pending-edit': (params: CellClassParams<T>) => {
            const field = params.colDef.field
            if (!field || !params.data) {
              return false
            }

            return Object.prototype.hasOwnProperty.call(
              pendingUpdates[getRowId(params.data)] ?? {},
              field
            )
          }
        }
      })),
    [columns, getRowId, isEditingDisabled, pendingUpdates]
  )

  return (
    <Box
      ref={setGridContainer}
      borderWidth='1px'
      borderColor='gray.400'
      borderRadius='md'
      overflow='hidden'
      sx={
        paginated
          ? {
              '.ag-paging-page-summary-panel': { whiteSpace: 'nowrap', flexShrink: 0 },
              '.ag-paging-row-summary-panel': { display: { base: 'none', md: 'block' } },
              '.ag-paging-panel': { justifyContent: { base: 'center', md: 'flex-end' } }
            }
          : undefined
      }
    >
      {gridContainer ? (
        <BaseGrid<T>
          rowData={draftRows}
          columns={columnsWithPendingEdits}
          getRowId={(params) => getRowId(params.data)}
          onCellValueChanged={(event: CellValueChangedEvent<T>) => {
            const field = event.colDef.field
            const oldValue = getComparableCellValue(event.colDef, event.oldValue)
            const newValue = getComparableCellValue(event.colDef, event.newValue)
            if (!field || oldValue === newValue || !event.data) {
              return
            }

            const rowId = getRowId(event.data)
            const originalRow = rows.find((row) => getRowId(row) === rowId)
            const originalValue = originalRow
              ? getComparableCellValue(
                  event.colDef,
                  (originalRow as Record<string, unknown>)[field]
                )
              : undefined
            onChange?.(rowId, field, newValue, Boolean(originalRow && newValue === originalValue))
          }}
          onRowClicked={(event: RowClickedEvent<T>) => {
            if (event.data) {
              onRowClicked?.(event.data)
            }
          }}
          onFilterChanged={(event) => {
            if (paginated) event.api.paginationGoToFirstPage()
          }}
          onModelUpdated={(event) => {
            if (!paginated) return
            if (event.api.getDisplayedRowCount() === 0) event.api.showNoRowsOverlay()
            else event.api.hideOverlay()
          }}
          quickFilterText={searchText.trim()}
          overlayNoRowsTemplate={paginated ? 'No dispositions to display.' : undefined}
          optionOverrides={{
            singleClickEdit: true,
            stopEditingWhenCellsLoseFocus: true,
            rowSelection: 'single',
            suppressRowClickSelection: false,
            sideBar: false,
            pagination: paginated,
            paginationPageSize: DISPOSITIONS_PAGE_SIZE,
            ...(paginated ? { rowHeight: DISPOSITIONS_ROW_HEIGHT } : {}),
            popupParent
          }}
          defaultColDefOverrides={{
            resizable: true,
            width: undefined,
            initialWidth: 200,
            ...(paginated ? { autoHeight: false } : {})
          }}
          tooltipShowDelay={200}
          containerStyle={{ height }}
          rowAutoHeight={rowAutoHeight}
        />
      ) : (
        <Box height={height} />
      )}
    </Box>
  )
}

const SaveBar = ({
  pendingCount,
  isSaving,
  onSave,
  onDiscard
}: {
  pendingCount: number
  isSaving: boolean
  onSave: () => void
  onDiscard: () => void
}) => {
  if (!pendingCount) {
    return null
  }

  return (
    <HStack justify='flex-start' spacing={3} mb={2}>
      <Text fontSize='sm' color='orange.700'>
        {pendingCount} unsaved row{pendingCount === 1 ? '' : 's'}
      </Text>
      <ButtonGroup size='sm' isDisabled={isSaving} spacing={2}>
        <Button leftIcon={<XMarkIcon className='h-4 w-4' />} variant='outline' onClick={onDiscard}>
          Discard
        </Button>
        <Button
          leftIcon={<CheckIcon className='h-4 w-4' />}
          colorScheme='teal'
          isLoading={isSaving}
          onClick={onSave}
        >
          Save
        </Button>
      </ButtonGroup>
    </HStack>
  )
}

const FormModal = ({
  title,
  submitLabel = 'Save',
  isOpen,
  onClose,
  fields,
  initialValues,
  onSubmit
}: {
  title: string
  submitLabel?: string
  isOpen: boolean
  onClose: () => void
  fields: FormField[]
  initialValues: FormValues
  onSubmit: (values: FormValues) => Promise<void>
}) => {
  const [values, setValues] = useState<FormValues>(initialValues)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setValues(initialValues)
      setFieldErrors({})
    }
    wasOpenRef.current = isOpen
  }, [initialValues, isOpen])

  const visibleFields = fields.filter((field) => !field.showWhen || field.showWhen(values))
  const setFieldValue = (name: string, value: string) => {
    setValues((current) => ({
      ...current,
      [name]: value
    }))
    setFieldErrors((current) => {
      if (!current[name]) {
        return current
      }

      const { [name]: _removed, ...next } = current
      return next
    })
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    const requiredErrors = visibleFields.reduce<Record<string, string>>((errors, field) => {
      const value = String(values[field.name] ?? '').trim()

      if (field.required && !value) {
        errors[field.name] = `${field.label} is required.`
      }

      if (field.type === 'number' && value) {
        const numberValue = Number(value)
        if (!Number.isFinite(numberValue)) {
          errors[field.name] = `${field.label} must be a number.`
        } else if (field.step === 1 && !Number.isInteger(numberValue)) {
          errors[field.name] = `${field.label} must be a whole number.`
        } else if (field.min != null && numberValue < field.min) {
          errors[field.name] = `${field.label} must be at least ${field.min}.`
        } else if (field.max != null && numberValue > field.max) {
          errors[field.name] = `${field.label} must be at most ${field.max}.`
        }
      }

      return errors
    }, {})

    if (Object.keys(requiredErrors).length) {
      setFieldErrors(requiredErrors)
      return
    }

    setIsSubmitting(true)
    try {
      await onSubmit(values)
      onClose()
    } catch {
      // Mutation helpers already surface a toast; keep the modal open for correction.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='xl' scrollBehavior='inside' isCentered>
      <ModalOverlay />
      <ModalContent maxH='calc(100vh - 2rem)' mx={{ base: 2, sm: 4 }} my={4}>
        <form
          onSubmit={submit}
          noValidate
          style={{
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'inherit',
            overflow: 'hidden'
          }}
        >
          <ModalHeader flexShrink={0}>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody minH={0} overflowY='auto'>
            <Stack spacing={4}>
              {visibleFields.map((field) => (
                <FormControl
                  key={field.name}
                  isRequired={field.required}
                  isInvalid={Boolean(fieldErrors[field.name])}
                >
                  <FormLabel>{field.label}</FormLabel>
                  {field.type === 'select' && field.searchable ? (
                    <SearchableFormSelect
                      value={values[field.name] ?? ''}
                      options={field.options ?? []}
                      placeholder={field.placeholder}
                      isInvalid={Boolean(fieldErrors[field.name])}
                      onChange={(value) => setFieldValue(field.name, value)}
                    />
                  ) : field.type === 'select' ? (
                    <Select
                      value={values[field.name] ?? ''}
                      onChange={(event) => setFieldValue(field.name, event.target.value)}
                    >
                      <option value=''>Select</option>
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  ) : field.type === 'textarea' ? (
                    <Textarea
                      value={values[field.name] ?? ''}
                      onChange={(event) => setFieldValue(field.name, event.target.value)}
                    />
                  ) : (
                    <Input
                      type={field.type}
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={values[field.name] ?? ''}
                      onChange={(event) => setFieldValue(field.name, event.target.value)}
                    />
                  )}
                  <FormErrorMessage>{fieldErrors[field.name]}</FormErrorMessage>
                </FormControl>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter flexShrink={0} borderTopWidth='1px' borderColor='gray.100'>
            <ButtonGroup>
              <Button variant='outline' onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme='teal' type='submit' isLoading={isSubmitting}>
                {submitLabel}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

const ConfirmModal = ({
  title,
  body,
  confirmLabel,
  isOpen,
  onClose,
  onConfirm,
  colorScheme = 'red'
}: {
  title: string
  body: React.ReactNode
  confirmLabel: string
  isOpen: boolean
  onClose: () => void
  onConfirm: () => Promise<void>
  colorScheme?: string
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const confirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm()
      onClose()
    } catch {
      // Confirm actions show their own toast on failure; keep the modal open.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>{body}</ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme={colorScheme} isLoading={isSubmitting} onClick={confirm}>
              {confirmLabel}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

const DetailsSection = ({
  title,
  actions,
  children
}: {
  title: string
  actions?: React.ReactNode
  children: React.ReactNode
}) => (
  <Box minW={0}>
    <HStack
      justify='flex-start'
      align='center'
      flexWrap='wrap'
      gap={2}
      mb={3}
      pb={2}
      borderBottomWidth='1px'
      borderColor='gray.200'
    >
      <Text fontWeight='semibold'>{title}</Text>
      {actions}
    </HStack>
    {children}
  </Box>
)

const DispositionsPageLayout = ({ children }: { children: React.ReactNode }) => (
  <Box width='100%' maxW='1440px' mx='auto'>
    <BasicPageLayout tabTitle='Construction | Dispositions' heading='Dispositions'>
      {children}
    </BasicPageLayout>
  </Box>
)

const Dispositions = () => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const canManageDispositions = hasCapability(admin, CAPABILITY_TYPES.ENGINEER)

  const [tabIndex, setTabIndex] = useState(0)
  const [dispositionSearch, setDispositionSearch] = useState('')
  const [selectedDisposition, setSelectedDisposition] = useState<DispositionSummaryRow | null>(null)
  const [selectedAgent, setSelectedAgent] = useState<DispositionAgent | null>(null)
  const [selectedBid, setSelectedBid] = useState<DispositionBidRow | null>(null)
  const [selectedFeedback, setSelectedFeedback] = useState<AgentFeedbackRow | null>(null)
  const [pendingDetailsNavigation, setPendingDetailsNavigation] =
    useState<PendingDetailsNavigation | null>(null)
  const [dispositionUpdates, setDispositionUpdates] = useState<Record<string, PatchUpdate>>({})
  const [agentUpdates, setAgentUpdates] = useState<Record<string, PatchUpdate>>({})
  const [listPriceUpdates, setListPriceUpdates] = useState<Record<string, PatchUpdate>>({})
  const [bidUpdates, setBidUpdates] = useState<Record<string, PatchUpdate>>({})
  const [feedbackUpdates, setFeedbackUpdates] = useState<Record<string, PatchUpdate>>({})
  const [dispositionGridResetKey, setDispositionGridResetKey] = useState(0)
  const [agentGridResetKey, setAgentGridResetKey] = useState(0)
  const [listPriceGridResetKey, setListPriceGridResetKey] = useState(0)
  const [bidGridResetKey, setBidGridResetKey] = useState(0)
  const [feedbackGridResetKey, setFeedbackGridResetKey] = useState(0)
  const detailsSectionRef = useRef<HTMLDivElement | null>(null)

  const createDispositionModal = useDisclosure()
  const createAgentModal = useDisclosure()
  const createListPriceModal = useDisclosure()
  const createBidModal = useDisclosure()
  const createFeedbackModal = useDisclosure()
  const deleteAgentModal = useDisclosure()
  const deleteFeedbackModal = useDisclosure()
  const markSoldModal = useDisclosure()
  const discardDetailsModal = useDisclosure()

  const scope: DispositionSummaryScope = tabIndex === 1 ? 'all' : 'active'

  const summaryQuery = useQuery({
    queryKey: ['dispositionsTrackerSummary', scope],
    enabled: canManageDispositions,
    queryFn: async () => {
      const response = await axios.get<DispositionsTrackerSummaryResponse>(`${API_ROOT}/summary`, {
        params: { scope }
      })
      return response.data
    }
  })

  const detailsQuery = useQuery({
    queryKey: ['dispositionDetails', selectedDisposition?.disposition_id],
    enabled: canManageDispositions && Boolean(selectedDisposition?.disposition_id),
    queryFn: async () => {
      const response = await axios.get<DispositionDetailsResponse>(
        `${API_ROOT}/dispositions/${selectedDisposition?.disposition_id}/details`
      )
      return response.data
    }
  })

  const summary = summaryQuery.data ?? EMPTY_SUMMARY
  const details = detailsQuery.data ?? EMPTY_DETAILS
  const pendingDetailEditCount =
    Object.keys(listPriceUpdates).length +
    Object.keys(bidUpdates).length +
    Object.keys(feedbackUpdates).length
  const detailsStateRef = useRef({ scope, pendingDetailEditCount })
  detailsStateRef.current = { scope, pendingDetailEditCount }
  const invalidateSummary = () =>
    queryClient.invalidateQueries({ queryKey: ['dispositionsTrackerSummary'] })
  const invalidateDetails = () =>
    queryClient.invalidateQueries({ queryKey: ['dispositionDetails'] })

  useEffect(() => {
    if (!selectedDisposition) {
      return
    }

    const refreshedDisposition = summary.dispositions.find(
      (disposition) => disposition.disposition_id === selectedDisposition.disposition_id
    )
    // Scope filters and temporary empty summaries must not close details or discard their drafts.
    if (refreshedDisposition && refreshedDisposition !== selectedDisposition) {
      setSelectedDisposition(refreshedDisposition)
    }
  }, [selectedDisposition, summary.dispositions])

  useEffect(() => {
    if (!selectedAgent || !summaryQuery.data) {
      return
    }

    const refreshedAgent = summary.agents.find((agent) => agent.id === selectedAgent.id)
    if (refreshedAgent && refreshedAgent !== selectedAgent) {
      setSelectedAgent(refreshedAgent)
    } else if (!refreshedAgent) {
      setSelectedAgent(null)
    }
  }, [selectedAgent, summary.agents, summaryQuery.data])

  useEffect(() => {
    if (!selectedBid) {
      return
    }

    const refreshedBid = details.bids.find((bid) => bid.id === selectedBid.id)
    if (refreshedBid && refreshedBid !== selectedBid) {
      setSelectedBid(refreshedBid)
    } else if (!refreshedBid) {
      setSelectedBid(null)
    }
  }, [details.bids, selectedBid])

  useEffect(() => {
    if (!selectedFeedback) {
      return
    }

    const refreshedFeedback = details.feedback.find(
      (feedback) => feedback.id === selectedFeedback.id
    )
    if (refreshedFeedback && refreshedFeedback !== selectedFeedback) {
      setSelectedFeedback(refreshedFeedback)
    } else if (!refreshedFeedback) {
      setSelectedFeedback(null)
    }
  }, [details.feedback, selectedFeedback])

  useEffect(() => {
    setListPriceUpdates({})
    setBidUpdates({})
    setFeedbackUpdates({})
    setSelectedBid(null)
    setSelectedFeedback(null)
  }, [selectedDisposition?.disposition_id])

  const patchMutation = useMutation({
    mutationFn: async ({ endpoint, updates }: { endpoint: string; updates: PatchUpdate[] }) =>
      axios.patch(`${API_ROOT}/${endpoint}`, { updates })
  })

  const postMutation = useMutation({
    mutationFn: async ({ endpoint, payload }: { endpoint: string; payload?: unknown }) =>
      axios.post(`${API_ROOT}/${endpoint}`, payload ?? {})
  })

  const deleteMutation = useMutation({
    mutationFn: async (endpoint: string) => axios.delete(`${API_ROOT}/${endpoint}`)
  })

  const updatePending = (
    setter: React.Dispatch<React.SetStateAction<Record<string, PatchUpdate>>>,
    id: string,
    field: string,
    value: unknown,
    isOriginalValue: boolean
  ) => {
    setter((current) => {
      if (isOriginalValue) {
        const currentRowUpdate = current[id]
        if (!currentRowUpdate || !Object.prototype.hasOwnProperty.call(currentRowUpdate, field)) {
          return current
        }

        const nextRowUpdate = { ...currentRowUpdate }
        delete nextRowUpdate[field]

        const next = { ...current }
        if (Object.keys(nextRowUpdate).length === 1) {
          delete next[id]
        } else {
          next[id] = nextRowUpdate
        }
        return next
      }

      return {
        ...current,
        [id]: {
          ...(current[id] ?? { id }),
          [field]: value
        }
      }
    })
  }

  const discardGridEdits = (
    clearUpdates: () => void,
    resetGrid: React.Dispatch<React.SetStateAction<number>>,
    afterDiscard?: () => void
  ) => {
    clearUpdates()
    resetGrid((current) => current + 1)
    afterDiscard?.()
  }

  const reconcileInactiveDetails = (dispositionId: string, status: string) => {
    setSelectedDisposition((current) => {
      if (current?.disposition_id !== dispositionId) {
        return current
      }

      // Read current scope/drafts: the user may have edited or navigated during the mutation.
      const latest = detailsStateRef.current
      return latest.scope === 'active' && !latest.pendingDetailEditCount
        ? null
        : { ...current, status }
    })
  }

  const refreshTracker = async ({ details = false }: { details?: boolean }) => {
    if (details) {
      await invalidateDetails()
      void invalidateSummary()
    } else {
      await invalidateSummary()
    }
  }

  const savePatch = async (
    endpoint: string,
    updates: Record<string, PatchUpdate>,
    clearUpdates: () => void,
    options: { details?: boolean } = {}
  ) => {
    try {
      await patchMutation.mutateAsync({ endpoint, updates: Object.values(updates) })
      clearUpdates()
      await refreshTracker(options)
      if (endpoint === 'dispositions' && selectedDisposition) {
        const status = updates[selectedDisposition.disposition_id]?.status
        if (status === DISPOSITION_STATUSES.CANCELED || status === DISPOSITION_STATUSES.SOLD) {
          reconcileInactiveDetails(selectedDisposition.disposition_id, status)
        }
      }
      toast({ title: 'Saved', status: 'success', duration: 2500, isClosable: true })
    } catch (error) {
      showToastError(toast, 'Unable to save changes', error)
    }
  }

  const postAndRefresh = async (
    endpoint: string,
    payload?: unknown,
    options: { details?: boolean } = {}
  ) => {
    try {
      await postMutation.mutateAsync({ endpoint, payload })
      await refreshTracker(options)
      toast({ title: 'Created', status: 'success', duration: 2500, isClosable: true })
    } catch (error) {
      showToastError(toast, 'Unable to create record', error)
      throw error
    }
  }

  const deleteAndRefresh = async (endpoint: string, options: { details?: boolean } = {}) => {
    try {
      await deleteMutation.mutateAsync(endpoint)
      await refreshTracker(options)
      toast({ title: 'Deleted', status: 'success', duration: 2500, isClosable: true })
    } catch (error) {
      showToastError(toast, 'Unable to delete record', error)
      throw error
    }
  }

  const selectDetails = useCallback((disposition: DispositionSummaryRow | null) => {
    setSelectedDisposition(disposition)
    if (disposition) {
      window.requestAnimationFrame(() => {
        detailsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }
  }, [])

  const requestDetailsNavigation = useCallback(
    (disposition: DispositionSummaryRow | null) => {
      if (
        pendingDetailEditCount &&
        disposition?.disposition_id !== selectedDisposition?.disposition_id
      ) {
        setPendingDetailsNavigation({ disposition })
        discardDetailsModal.onOpen()
        return
      }

      selectDetails(disposition)
    },
    [
      discardDetailsModal,
      pendingDetailEditCount,
      selectDetails,
      selectedDisposition?.disposition_id
    ]
  )

  const openDetails = useCallback(
    (disposition: DispositionSummaryRow) => requestDetailsNavigation(disposition),
    [requestDetailsNavigation]
  )

  const hasPendingDetailEdits = pendingDetailEditCount > 0
  const selectedBidCannotBeSold =
    selectedBid?.status === 'canceled' || selectedBid?.status === 'terminated'

  const statusOptions = useMemo(
    () =>
      [...summary.editableStatuses]
        .sort((a, b) => {
          const aRank = EDITABLE_DISPOSITION_STATUS_RANK.get(a.status) ?? Number.MAX_SAFE_INTEGER
          const bRank = EDITABLE_DISPOSITION_STATUS_RANK.get(b.status) ?? Number.MAX_SAFE_INTEGER
          return aRank - bRank || a.status.localeCompare(b.status)
        })
        .map((status) => ({
          label: formatDispositionStatusLabel(status.status),
          value: status.status
        })),
    [summary.editableStatuses]
  )
  const createStatusOptions = useMemo(
    () => statusOptions.filter((option) => option.value !== DISPOSITION_STATUSES.CANCELED),
    [statusOptions]
  )
  const agentOptions = useMemo(
    () =>
      summary.agents.map((agent) => ({
        label: agent.name,
        description: [agent.brokerage, agent.phone].filter(Boolean).join(' | '),
        value: agent.id
      })),
    [summary.agents]
  )
  const agentOptionsWithOther = [
    ...agentOptions,
    {
      label: 'Other',
      value: 'other',
      description: 'Create a new agent from the fields below.'
    }
  ]
  const counterpartyOptions = useMemo(
    () =>
      summary.counterparties.map((counterparty) => ({
        label: counterparty.name,
        description: counterparty.type ?? undefined,
        value: counterparty.id
      })),
    [summary.counterparties]
  )
  const counterpartyOptionsWithOther = [
    ...counterpartyOptions,
    {
      label: 'Other',
      value: 'other',
      description: 'Create a new buyer from the fields below.'
    }
  ]
  const counterpartyTypeOptions = summary.counterpartyTypes.map((counterpartyType) => ({
    label: counterpartyType.type,
    value: counterpartyType.type
  }))
  const getAgentName = useCallback(
    (agentId: string | null | undefined, fallbackName: string | null = null) =>
      summary.agents.find((agent) => agent.id === agentId)?.name ?? fallbackName ?? '',
    [summary.agents]
  )
  const soldDispositionIds = useMemo(
    () =>
      new Set(
        summary.dispositions
          .filter((row) => row.status === DISPOSITION_STATUSES.SOLD)
          .map((row) => row.disposition_id)
      ),
    [summary.dispositions]
  )
  const discardDispositionStatusEdit = useCallback((id: string) => {
    setDispositionUpdates((current) => {
      if (!Object.prototype.hasOwnProperty.call(current[id] ?? {}, 'status')) return current
      const remaining = { ...current[id] }
      delete remaining.status
      const next = { ...current }
      if (Object.keys(remaining).some((field) => field !== 'id')) next[id] = remaining
      else delete next[id]
      return next
    })
  }, [])
  const dispositionColumns = useMemo<ColDef<DispositionSummaryRow>[]>(
    () => [
      {
        headerName: '',
        colId: 'details',
        width: 140,
        minWidth: 140,
        maxWidth: 140,
        resizable: false,
        suppressSizeToFit: true,
        sortable: false,
        filter: false,
        cellRenderer: (params: ICellRendererParams<DispositionSummaryRow>) =>
          params.data ? (
            <Button
              size='xs'
              variant='outline'
              leftIcon={<ChevronDownIcon className='h-4 w-4' />}
              onClick={(event) => {
                event.stopPropagation()
                openDetails(params.data!)
              }}
            >
              Details
            </Button>
          ) : null
      },
      { field: 'address', headerName: 'Address', initialWidth: 260 },
      {
        field: 'market',
        headerName: 'Market',
        initialWidth: 160,
        minWidth: 140,
        filter: SetFilter,
        cellRenderer: badgeCellRenderer
      },
      {
        field: 'fund',
        headerName: 'Fund',
        initialWidth: 200,
        minWidth: 200,
        filter: SetFilter,
        cellRenderer: badgeCellRenderer
      },
      markEditableColumn({
        field: 'status',
        headerName: 'Status',
        valueGetter: ({ data }) =>
          data && soldDispositionIds.has(data.disposition_id)
            ? DISPOSITION_STATUSES.SOLD
            : data?.status,
        editable: ({ data }) => !!data && !soldDispositionIds.has(data.disposition_id),
        tooltipValueGetter: ({ data }) =>
          data && soldDispositionIds.has(data.disposition_id)
            ? 'Sold disposition status cannot be changed.'
            : 'Click to edit Status.',
        filter: SetFilter,
        cellRenderer: (params: ICellRendererParams<DispositionSummaryRow>) => {
          const id = params.data?.disposition_id
          if (!id || !soldDispositionIds.has(id)) return badgeCellRenderer(params)
          return (
            <HStack spacing={1}>
              {badgeCellRenderer({ ...params, value: DISPOSITION_STATUSES.SOLD })}
              {Object.prototype.hasOwnProperty.call(dispositionUpdates[id] ?? {}, 'status') ? (
                <Tooltip label='Discard outdated status edit'>
                  <IconButton
                    aria-label='Discard status edit'
                    size='xs'
                    variant='ghost'
                    icon={<ArrowUturnLeftIcon className='h-4 w-4' />}
                    onClick={(event) => {
                      event.stopPropagation()
                      discardDispositionStatusEdit(id)
                    }}
                  />
                </Tooltip>
              ) : null}
            </HStack>
          )
        },
        ...badgeSelectCellEditor(statusOptions.map((option) => option.value))
      }),
      markDetailsManagedColumn(
        {
          field: 'estimated_closing_date',
          headerName: 'Est. Closing',
          valueFormatter: valueFormatter(formatDate)
        },
        "Click Details, then update a bid's estimated closing date."
      ),
      markEditableColumn({
        field: 'sale_type',
        headerName: 'Sale Type',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor(['', ...saleTypeOptions.map((option) => option.value)])
      }),
      markEditableColumn({
        field: 'is_staged',
        headerName: 'Staged',
        cellEditor: BooleanCellEditor,
        valueFormatter: yesNoFormatter
      }),
      markEditableColumn({
        field: 'target_price',
        headerName: 'Target Price',
        valueFormatter: valueFormatter(formatMoney)
      }),
      markDetailsManagedColumn(
        {
          field: 'current_listed_price',
          headerName: 'Current List Price',
          valueFormatter: valueFormatter(formatMoney)
        },
        'Click Details, then add or edit a list price to update the current list price.'
      ),
      markDetailsManagedColumn(
        {
          field: 'original_list_date',
          headerName: 'Original List Date',
          valueFormatter: valueFormatter(formatDate)
        },
        'Derived from list price history. Click Details to manage list prices.'
      ),
      markDetailsManagedColumn(
        {
          field: 'relisted_date',
          headerName: 'Relisted Date',
          valueFormatter: valueFormatter(formatDate)
        },
        'Derived from list price history. Click Details to manage list prices.'
      ),
      markDetailsManagedColumn(
        { field: 'days_on_market', headerName: 'DOM', initialWidth: 100 },
        'Derived from list price history and listing status. Click Details to manage list prices.'
      ),
      markDetailsManagedColumn(
        {
          field: 'closing_date',
          headerName: 'Closing Date',
          valueFormatter: valueFormatter(formatDate)
        },
        "Click Details, then update a bid's closing date or mark a bid sold."
      ),
      markDetailsManagedColumn(
        {
          field: 'sale_price',
          headerName: 'Sale Price',
          valueFormatter: valueFormatter(formatMoney)
        },
        'Populated from the sold bid. Click Details to manage bids or mark a bid sold.'
      ),
      {
        field: 'fair_market_value',
        headerName: 'FMV',
        valueFormatter: valueFormatter(formatMoney)
      },
      { field: 'all_in_cost', headerName: 'AIC', valueFormatter: valueFormatter(formatMoney) }
    ],
    [
      discardDispositionStatusEdit,
      dispositionUpdates,
      openDetails,
      soldDispositionIds,
      statusOptions
    ]
  )

  const agentColumns = useMemo<ColDef<DispositionAgent>[]>(
    () => [
      markEditableColumn({
        field: 'name',
        headerName: 'Name',
        initialWidth: 280,
        minWidth: 200
      }),
      markEditableColumn({ field: 'brokerage', headerName: 'Brokerage', flex: 2, minWidth: 200 }),
      markEditableColumn({ field: 'phone', headerName: 'Phone', flex: 1, minWidth: 160 })
    ],
    []
  )

  const listPriceColumns = useMemo<ColDef<ListPriceHistoryRow>[]>(
    () => [
      markEditableDateColumn({
        field: 'date',
        headerName: 'Date',
        flex: 1,
        minWidth: 160
      }),
      markEditableColumn({
        field: 'price',
        headerName: 'Price',
        flex: 1,
        minWidth: 140,
        valueFormatter: valueFormatter(formatMoney)
      })
    ],
    []
  )

  const bidColumns = useMemo<ColDef<DispositionBidRow>[]>(
    () => [
      markEditableColumn({
        field: 'counterparty_id',
        headerName: 'Buyer',
        initialWidth: 240,
        cellEditor: SearchableCellEditor,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        cellEditorParams: { values: counterpartyOptions, placeholder: 'Search for a buyer...' },
        valueFormatter: ({ value }) =>
          summary.counterparties.find((counterparty) => counterparty.id === value)?.name ?? ''
      }),
      markDetailsManagedColumn(
        {
          field: 'status',
          headerName: 'Status',
          initialWidth: 140,
          minWidth: 140,
          filter: SetFilter,
          cellRenderer: badgeCellRenderer
        },
        'Derived from the bid dates. Add a Bind Date to mark it accepted, add a Terminated Date to mark it terminated, or use Mark Sold to close it.'
      ),
      markEditableColumn({
        field: 'bid_price',
        headerName: 'Bid Price',
        initialWidth: 150,
        minWidth: 140,
        valueFormatter: valueFormatter(formatMoney)
      }),
      markEditableDateColumn({
        field: 'estimated_closing_date',
        headerName: 'Est. Closing'
      }),
      markDetailsManagedColumn(
        {
          field: 'counterparty_type',
          headerName: 'Buyer Type',
          filter: SetFilter,
          cellRenderer: badgeCellRenderer
        },
        'Derived from Buyer. Edit the Buyer field to change this value.'
      ),
      markEditableColumn({
        field: 'agent_id',
        headerName: 'Agent',
        initialWidth: 240,
        cellEditor: SearchableCellEditor,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        cellEditorParams: {
          values: agentOptions,
          allowEmpty: true,
          placeholder: 'Search for an agent...'
        },
        valueFormatter: ({ value, data }) => getAgentName(value, data?.agent_name)
      }),
      markDetailsManagedColumn(
        { field: 'agent_brokerage', headerName: 'Brokerage' },
        'Derived from Agent. Edit the Agent field to change this value.'
      ),
      markEditableColumn({
        field: 'total_sellers_credits',
        headerName: 'Seller Credits',
        valueFormatter: valueFormatter(formatMoney)
      }),
      markEditableColumn({
        field: 'earnest_money_amount',
        headerName: 'Earnest Money',
        valueFormatter: valueFormatter(formatMoney)
      }),
      markEditableDateColumn({
        field: 'bid_received_date',
        headerName: 'Received'
      }),
      markEditableDateColumn({
        field: 'bind_date',
        headerName: 'Bind Date'
      }),
      markEditableDateColumn({
        field: 'due_diligence_end_date',
        headerName: 'DD End'
      }),
      markEditableDateColumn({
        field: 'closing_date',
        headerName: 'Closing'
      }),
      markEditableDateColumn({
        field: 'terminated_date',
        headerName: 'Terminated'
      })
    ],
    [agentOptions, counterpartyOptions, getAgentName, summary.counterparties]
  )

  const feedbackColumns = useMemo<ColDef<AgentFeedbackRow>[]>(
    () => [
      markEditableColumn({
        field: 'agent_id',
        headerName: 'Agent',
        initialWidth: 240,
        cellEditor: SearchableCellEditor,
        cellEditorPopup: true,
        cellEditorPopupPosition: 'under',
        cellEditorParams: { values: agentOptions, placeholder: 'Search for an agent...' },
        valueFormatter: ({ value, data }) => getAgentName(value, data?.agent_name)
      }),
      markDetailsManagedColumn(
        { field: 'agent_brokerage', headerName: 'Brokerage' },
        'Derived from Agent. Edit the Agent field to change this value.'
      ),
      markEditableColumn({
        field: 'counterparty_type',
        headerName: 'Counterparty Type',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor(['', ...summary.counterpartyTypes.map((type) => type.type)])
      }),
      markEditableDateColumn({
        field: 'showing_date',
        headerName: 'Showing Date'
      }),
      markEditableColumn({
        field: 'status',
        headerName: 'Status',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor(AGENT_FEEDBACK_STATUS_OPTIONS.map((option) => option.value))
      }),
      markEditableColumn({
        field: 'interest',
        headerName: 'Interest',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor([
          '',
          ...AGENT_FEEDBACK_INTEREST_OPTIONS.map((option) => option.value)
        ])
      }),
      markEditableColumn({
        field: 'overall_experience',
        headerName: 'Experience',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor([
          '',
          ...AGENT_FEEDBACK_EXPERIENCE_OPTIONS.map((option) => option.value)
        ])
      }),
      markEditableColumn({
        field: 'price_opinion',
        headerName: 'Price Opinion',
        filter: SetFilter,
        cellRenderer: badgeCellRenderer,
        ...badgeSelectCellEditor([
          '',
          ...AGENT_FEEDBACK_PRICE_OPINION_OPTIONS.map((option) => option.value)
        ])
      }),
      markEditableColumn({
        field: 'rating',
        headerName: 'Rating',
        initialWidth: 120,
        cellEditor: RatingCellEditor,
        valueFormatter: ({ value }) => (value == null || value === '' ? '' : `${value} / 5`)
      }),
      markEditableColumn({
        field: 'comments',
        headerName: 'Comments',
        initialWidth: 420,
        minWidth: 320,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '20px' }
      }),
      markEditableColumn({
        field: 'feedback',
        headerName: 'Feedback',
        initialWidth: 420,
        minWidth: 320,
        wrapText: true,
        autoHeight: true,
        cellStyle: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: '20px' }
      })
    ],
    [agentOptions, getAgentName, summary.counterpartyTypes]
  )

  if (!canManageDispositions) {
    return (
      <DispositionsPageLayout>
        <Box mt={6}>
          <Alert status='warning'>
            <AlertIcon />
            You do not have access to the Dispositions Tracker.
          </Alert>
        </Box>
      </DispositionsPageLayout>
    )
  }

  if (summaryQuery.isLoading) {
    return (
      <DispositionsPageLayout>
        <Center minH='320px'>
          <Spinner />
        </Center>
      </DispositionsPageLayout>
    )
  }

  if (summaryQuery.isError) {
    return (
      <DispositionsPageLayout>
        <Box mt={6}>
          <Alert status='error'>
            <AlertIcon />
            <Stack spacing={3}>
              <Text>
                Unable to load the Dispositions Tracker: {getErrorMessage(summaryQuery.error)}
              </Text>
              <Button size='sm' width='fit-content' onClick={() => summaryQuery.refetch()}>
                Retry
              </Button>
            </Stack>
          </Alert>
        </Box>
      </DispositionsPageLayout>
    )
  }

  const dispositionPanel = (
    <TabPanel px={0}>
      <HStack spacing={3} mb={3} flexWrap='wrap' rowGap={3}>
        <InputGroup size='sm' maxW='420px'>
          <InputLeftElement pointerEvents='none' color='gray.500'>
            <MagnifyingGlassIcon className='h-4 w-4' />
          </InputLeftElement>
          <Input
            aria-label='Search dispositions'
            placeholder='Search address, market, fund...'
            value={dispositionSearch}
            onChange={(event) => setDispositionSearch(event.target.value)}
            pr={10}
          />
          {dispositionSearch ? (
            <InputRightElement>
              <Tooltip label='Clear search'>
                <IconButton
                  aria-label='Clear search'
                  size='xs'
                  variant='ghost'
                  icon={<XMarkIcon className='h-4 w-4' />}
                  onClick={() => setDispositionSearch('')}
                />
              </Tooltip>
            </InputRightElement>
          ) : null}
        </InputGroup>
        <Button
          size='sm'
          leftIcon={<PlusIcon className='h-4 w-4' />}
          colorScheme='teal'
          onClick={createDispositionModal.onOpen}
        >
          Disposition
        </Button>
      </HStack>
      <SaveBar
        pendingCount={Object.keys(dispositionUpdates).length}
        isSaving={patchMutation.isLoading}
        onDiscard={() =>
          discardGridEdits(
            () => setDispositionUpdates({}),
            setDispositionGridResetKey,
            () =>
              setSelectedDisposition((current) =>
                current
                  ? summary.dispositions.find(
                      (row) => row.disposition_id === current.disposition_id
                    ) ?? current
                  : null
              )
          )
        }
        onSave={() =>
          savePatch('dispositions', dispositionUpdates, () => setDispositionUpdates({}))
        }
      />
      <EditableGrid
        rows={summary.dispositions}
        columns={dispositionColumns}
        getRowId={getDispositionRowId}
        onChange={(id, field, value, isOriginalValue) =>
          updatePending(setDispositionUpdates, id, field, value, isOriginalValue)
        }
        pendingUpdates={dispositionUpdates}
        resetKey={dispositionGridResetKey}
        height={DISPOSITIONS_GRID_HEIGHT}
        paginated
        searchText={dispositionSearch}
        isEditingDisabled={patchMutation.isLoading}
      />
    </TabPanel>
  )

  return (
    <DispositionsPageLayout>
      <Stack spacing={4} mt={4}>
        <Tabs index={tabIndex} onChange={setTabIndex} colorScheme='teal'>
          <TabList overflowX='auto' overflowY='hidden'>
            <Tab whiteSpace='nowrap' flexShrink={0}>
              Active Dispositions
            </Tab>
            <Tab whiteSpace='nowrap' flexShrink={0}>
              All Dispositions
            </Tab>
            <Tab whiteSpace='nowrap' flexShrink={0}>
              View/Edit Agents
            </Tab>
          </TabList>
          <TabPanels>
            {dispositionPanel}
            {dispositionPanel}
            <TabPanel px={0} maxW='960px'>
              <HStack spacing={2} mb={3}>
                <Button
                  size='sm'
                  leftIcon={<PlusIcon className='h-4 w-4' />}
                  colorScheme='teal'
                  onClick={createAgentModal.onOpen}
                >
                  Agent
                </Button>
                <Button
                  size='sm'
                  leftIcon={<TrashIcon className='h-4 w-4' />}
                  colorScheme='red'
                  variant='outline'
                  isDisabled={!selectedAgent}
                  onClick={deleteAgentModal.onOpen}
                >
                  Delete Agent
                </Button>
              </HStack>
              <SaveBar
                pendingCount={Object.keys(agentUpdates).length}
                isSaving={patchMutation.isLoading}
                onDiscard={() =>
                  discardGridEdits(
                    () => setAgentUpdates({}),
                    setAgentGridResetKey,
                    () =>
                      setSelectedAgent((current) =>
                        current
                          ? summary.agents.find((agent) => agent.id === current.id) ?? null
                          : null
                      )
                  )
                }
                onSave={() => savePatch('agents', agentUpdates, () => setAgentUpdates({}))}
              />
              <EditableGrid
                rows={summary.agents}
                columns={agentColumns}
                getRowId={getEntityRowId}
                onChange={(id, field, value, isOriginalValue) =>
                  updatePending(setAgentUpdates, id, field, value, isOriginalValue)
                }
                onRowClicked={setSelectedAgent}
                pendingUpdates={agentUpdates}
                resetKey={agentGridResetKey}
                height={620}
                isEditingDisabled={patchMutation.isLoading}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Stack>

      {tabIndex !== 2 && selectedDisposition ? (
        <Box
          ref={detailsSectionRef}
          mt={6}
          borderWidth='2px'
          borderColor='gray.400'
          borderRadius='md'
          bg='white'
          overflow='hidden'
          scrollMarginTop={4}
        >
          <Stack spacing={0}>
            <Stack
              direction={{ base: 'column', md: 'row' }}
              justify='space-between'
              align={{ base: 'stretch', md: 'flex-start' }}
              spacing={3}
              px={{ base: 4, md: 5 }}
              py={4}
              bg='gray.100'
              borderBottomWidth='1px'
              borderColor='gray.300'
            >
              <Stack spacing={2}>
                <HStack flexWrap='wrap'>
                  <Text fontSize='lg' fontWeight='semibold'>
                    {selectedDisposition.address}
                  </Text>
                  {mapContentToBadge(selectedDisposition.status, { dot: false, size: 'xs' })}
                </HStack>
                <HStack spacing={4} fontSize='sm' color='gray.600' flexWrap='wrap' rowGap={1}>
                  <Text>{selectedDisposition.market}</Text>
                  <Text>{selectedDisposition.fund}</Text>
                  <ChakraLink
                    href={`/home/${selectedDisposition.portfolio_home_id}`}
                    target='_blank'
                    rel='noreferrer'
                    color='teal.600'
                  >
                    <HStack spacing={1}>
                      <span>Home profile</span>
                      <ArrowTopRightOnSquareIcon className='h-4 w-4' />
                    </HStack>
                  </ChakraLink>
                </HStack>
              </Stack>
              <Button
                size='sm'
                leftIcon={<XMarkIcon className='h-4 w-4' />}
                color='white'
                bg='gray.700'
                _hover={{ bg: 'gray.800' }}
                flexShrink={0}
                alignSelf={{ base: 'flex-start', md: 'auto' }}
                onClick={() => requestDetailsNavigation(null)}
              >
                Close details
              </Button>
            </Stack>

            <Box p={{ base: 4, md: 5 }}>
              {detailsQuery.isError ? (
                <Alert status='error'>
                  <AlertIcon />
                  <Stack spacing={3}>
                    <Text>
                      Unable to load disposition details: {getErrorMessage(detailsQuery.error)}
                    </Text>
                    <Button size='sm' width='fit-content' onClick={() => detailsQuery.refetch()}>
                      Retry
                    </Button>
                  </Stack>
                </Alert>
              ) : detailsQuery.isLoading || (detailsQuery.isFetching && !detailsQuery.data) ? (
                <Center minH='240px'>
                  <Spinner />
                </Center>
              ) : (
                <Stack spacing={6}>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems='start'>
                    <SimpleGrid columns={2} spacing={6}>
                      <Box>
                        <Text fontSize='xs' color='gray.500'>
                          Target Price
                        </Text>
                        <Text fontWeight='semibold'>
                          {formatMoney(selectedDisposition.target_price)}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500'>
                          Current List
                        </Text>
                        <Text fontWeight='semibold'>
                          {formatMoney(selectedDisposition.current_listed_price)}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500'>
                          FMV
                        </Text>
                        <Text fontWeight='semibold'>
                          {formatMoney(selectedDisposition.fair_market_value)}
                        </Text>
                      </Box>
                      <Box>
                        <Text fontSize='xs' color='gray.500'>
                          AIC
                        </Text>
                        <Text fontWeight='semibold'>
                          {formatMoney(selectedDisposition.all_in_cost)}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    <DetailsSection
                      title='List Prices'
                      actions={
                        <Button
                          size='xs'
                          leftIcon={<PlusIcon className='h-4 w-4' />}
                          onClick={createListPriceModal.onOpen}
                        >
                          List Price
                        </Button>
                      }
                    >
                      <SaveBar
                        pendingCount={Object.keys(listPriceUpdates).length}
                        isSaving={patchMutation.isLoading}
                        onDiscard={() =>
                          discardGridEdits(() => setListPriceUpdates({}), setListPriceGridResetKey)
                        }
                        onSave={() =>
                          savePatch(
                            'list-prices',
                            listPriceUpdates,
                            () => setListPriceUpdates({}),
                            {
                              details: true
                            }
                          )
                        }
                      />
                      <EditableGrid
                        rows={details.listPrices}
                        columns={listPriceColumns}
                        getRowId={getEntityRowId}
                        onChange={(id, field, value, isOriginalValue) =>
                          updatePending(setListPriceUpdates, id, field, value, isOriginalValue)
                        }
                        pendingUpdates={listPriceUpdates}
                        resetKey={listPriceGridResetKey}
                        height={getDetailsGridHeight(details.listPrices.length, 220)}
                        isEditingDisabled={patchMutation.isLoading}
                      />
                    </DetailsSection>
                  </SimpleGrid>

                  <DetailsSection
                    title='Bids'
                    actions={
                      <ButtonGroup size='xs'>
                        <Button
                          leftIcon={<PlusIcon className='h-4 w-4' />}
                          onClick={createBidModal.onOpen}
                        >
                          Bid
                        </Button>
                        <Button
                          leftIcon={<CheckIcon className='h-4 w-4' />}
                          colorScheme='teal'
                          variant='outline'
                          isDisabled={
                            !selectedBid || hasPendingDetailEdits || selectedBidCannotBeSold
                          }
                          title={
                            hasPendingDetailEdits
                              ? 'Save or discard all detail edits before marking a bid sold.'
                              : selectedBidCannotBeSold
                              ? 'Canceled or terminated bids cannot be marked sold.'
                              : undefined
                          }
                          onClick={markSoldModal.onOpen}
                        >
                          Mark Sold
                        </Button>
                      </ButtonGroup>
                    }
                  >
                    <SaveBar
                      pendingCount={Object.keys(bidUpdates).length}
                      isSaving={patchMutation.isLoading}
                      onDiscard={() =>
                        discardGridEdits(
                          () => setBidUpdates({}),
                          setBidGridResetKey,
                          () =>
                            setSelectedBid((current) =>
                              current
                                ? details.bids.find((bid) => bid.id === current.id) ?? null
                                : null
                            )
                        )
                      }
                      onSave={() =>
                        savePatch('bids', bidUpdates, () => setBidUpdates({}), { details: true })
                      }
                    />
                    <EditableGrid
                      rows={details.bids}
                      columns={bidColumns}
                      getRowId={getEntityRowId}
                      onChange={(id, field, value, isOriginalValue) =>
                        updatePending(setBidUpdates, id, field, value, isOriginalValue)
                      }
                      onRowClicked={setSelectedBid}
                      pendingUpdates={bidUpdates}
                      resetKey={bidGridResetKey}
                      height={getDetailsGridHeight(details.bids.length, 320)}
                      isEditingDisabled={patchMutation.isLoading}
                    />
                  </DetailsSection>

                  <DetailsSection
                    title='Agent Feedback'
                    actions={
                      <ButtonGroup size='xs'>
                        <Button
                          leftIcon={<PlusIcon className='h-4 w-4' />}
                          onClick={createFeedbackModal.onOpen}
                        >
                          Feedback
                        </Button>
                        <Button
                          leftIcon={<TrashIcon className='h-4 w-4' />}
                          colorScheme='red'
                          variant='outline'
                          isDisabled={!selectedFeedback}
                          onClick={deleteFeedbackModal.onOpen}
                        >
                          Delete
                        </Button>
                      </ButtonGroup>
                    }
                  >
                    <SaveBar
                      pendingCount={Object.keys(feedbackUpdates).length}
                      isSaving={patchMutation.isLoading}
                      onDiscard={() =>
                        discardGridEdits(() => setFeedbackUpdates({}), setFeedbackGridResetKey)
                      }
                      onSave={() =>
                        savePatch('agent-feedback', feedbackUpdates, () => setFeedbackUpdates({}), {
                          details: true
                        })
                      }
                    />
                    <EditableGrid
                      rows={details.feedback}
                      columns={feedbackColumns}
                      getRowId={getEntityRowId}
                      onChange={(id, field, value, isOriginalValue) =>
                        updatePending(setFeedbackUpdates, id, field, value, isOriginalValue)
                      }
                      onRowClicked={setSelectedFeedback}
                      pendingUpdates={feedbackUpdates}
                      resetKey={feedbackGridResetKey}
                      height={Math.min(440, Math.max(220, 92 + details.feedback.length * 80))}
                      rowAutoHeight
                      isEditingDisabled={patchMutation.isLoading}
                    />
                  </DetailsSection>
                </Stack>
              )}
            </Box>
          </Stack>
        </Box>
      ) : null}

      <FormModal
        title='Create Disposition'
        submitLabel='Create'
        isOpen={createDispositionModal.isOpen}
        onClose={createDispositionModal.onClose}
        initialValues={{
          portfolio_home_id: '',
          status: createStatusOptions[0]?.value ?? '',
          sale_type: '',
          is_staged: '',
          target_price: ''
        }}
        fields={[
          {
            name: 'portfolio_home_id',
            label: 'Home',
            type: 'select',
            searchable: true,
            placeholder: 'Search by address or market...',
            required: true,
            options: summary.homesNotInDisposition.map((home) => ({
              label: home.address,
              description: home.market ?? undefined,
              value: home.portfolio_home_id
            }))
          },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            required: true,
            options: createStatusOptions
          },
          { name: 'sale_type', label: 'Sale Type', type: 'select', options: saleTypeOptions },
          {
            name: 'is_staged',
            label: 'Staged',
            type: 'select',
            options: [
              { label: 'Yes', value: 'true' },
              { label: 'No', value: 'false' }
            ]
          },
          { name: 'target_price', label: 'Target Price', type: 'number' }
        ]}
        onSubmit={async (values) => {
          const payload: CreateDispositionPayload = {
            portfolio_home_id: values.portfolio_home_id,
            status: values.status,
            sale_type: values.sale_type || null,
            is_staged: values.is_staged ? values.is_staged === 'true' : null,
            target_price: values.target_price ? Number(values.target_price) : null
          }
          await postAndRefresh('dispositions', payload)
        }}
      />

      <FormModal
        title='Create Agent'
        submitLabel='Create'
        isOpen={createAgentModal.isOpen}
        onClose={createAgentModal.onClose}
        initialValues={{ name: '', brokerage: '', phone: '' }}
        fields={[
          { name: 'name', label: 'Name', type: 'text', required: true },
          { name: 'brokerage', label: 'Brokerage', type: 'text' },
          { name: 'phone', label: 'Phone', type: 'text' }
        ]}
        onSubmit={async (values) => {
          const payload: CreateDispositionAgentPayload = {
            name: values.name,
            brokerage: values.brokerage || null,
            phone: values.phone || null
          }
          await postAndRefresh('agents', payload)
        }}
      />

      <FormModal
        title='Add List Price'
        submitLabel='Create'
        isOpen={createListPriceModal.isOpen}
        onClose={createListPriceModal.onClose}
        initialValues={{
          price: '',
          date: toLocalInputDate(new Date())
        }}
        fields={[
          { name: 'price', label: 'Price', type: 'number', required: true },
          { name: 'date', label: 'Date', type: 'date', required: true }
        ]}
        onSubmit={async (values) => {
          if (!selectedDisposition) {
            return
          }

          const payload: CreateListPricePayload = {
            portfolio_home_disposition_id: selectedDisposition.disposition_id,
            price: Number(values.price),
            date: values.date
          }
          await postAndRefresh('list-prices', payload, { details: true })
        }}
      />

      <FormModal
        title='Add Bid'
        submitLabel='Create'
        isOpen={createBidModal.isOpen}
        onClose={createBidModal.onClose}
        initialValues={{
          bid_price: '',
          bid_received_date: toLocalInputDate(new Date()),
          total_seller_credits: '0',
          agent_id: '',
          agent_name: '',
          agent_brokerage: '',
          agent_phone: '',
          buyer_id: '',
          buyer_name: '',
          buyer_type: ''
        }}
        fields={[
          { name: 'bid_price', label: 'Bid Price', type: 'number', required: true },
          { name: 'bid_received_date', label: 'Received Date', type: 'date', required: true },
          { name: 'total_seller_credits', label: 'Seller Credits', type: 'number' },
          ...agentFormFields(agentOptionsWithOther),
          {
            name: 'buyer_id',
            label: 'Buyer',
            type: 'select',
            searchable: true,
            placeholder: 'Search buyers...',
            required: true,
            options: counterpartyOptionsWithOther
          },
          {
            name: 'buyer_name',
            label: 'New Buyer Name',
            type: 'text',
            required: true,
            showWhen: (values) => values.buyer_id === 'other'
          },
          {
            name: 'buyer_type',
            label: 'New Buyer Type',
            type: 'select',
            required: true,
            options: counterpartyTypeOptions,
            showWhen: (values) => values.buyer_id === 'other'
          }
        ]}
        onSubmit={async (values) => {
          if (!selectedDisposition) {
            return
          }

          const payload: CreateDispositionBidPayload = {
            portfolio_home_disposition_id: selectedDisposition.disposition_id,
            bid_price: Number(values.bid_price),
            bid_received_date: values.bid_received_date,
            total_seller_credits: values.total_seller_credits
              ? Number(values.total_seller_credits)
              : 0,
            agent: getAgentPayload(values),
            buyer:
              values.buyer_id === 'other'
                ? {
                    id: 'other',
                    name: values.buyer_name,
                    type: values.buyer_type
                  }
                : {
                    id: values.buyer_id
                  }
          }
          await postAndRefresh('bids', payload, { details: true })
        }}
      />

      <FormModal
        title='Add Agent Feedback'
        submitLabel='Create'
        isOpen={createFeedbackModal.isOpen}
        onClose={createFeedbackModal.onClose}
        initialValues={{
          agent_id: '',
          agent_name: '',
          agent_brokerage: '',
          agent_phone: '',
          counterparty_type: '',
          showing_date: toLocalInputDate(new Date()),
          status: 'completed',
          interest: '',
          overall_experience: '',
          price_opinion: '',
          rating: '',
          comments: '',
          feedback: ''
        }}
        fields={[
          ...agentFormFields(agentOptionsWithOther),
          {
            name: 'counterparty_type',
            label: 'Counterparty Type',
            type: 'select',
            options: counterpartyTypeOptions
          },
          { name: 'showing_date', label: 'Showing Date', type: 'date', required: true },
          {
            name: 'status',
            label: 'Status',
            type: 'select',
            required: true,
            options: AGENT_FEEDBACK_STATUS_OPTIONS
          },
          {
            name: 'interest',
            label: 'Interest',
            type: 'select',
            options: AGENT_FEEDBACK_INTEREST_OPTIONS
          },
          {
            name: 'overall_experience',
            label: 'Experience',
            type: 'select',
            options: AGENT_FEEDBACK_EXPERIENCE_OPTIONS
          },
          {
            name: 'price_opinion',
            label: 'Price Opinion',
            type: 'select',
            options: AGENT_FEEDBACK_PRICE_OPINION_OPTIONS
          },
          { name: 'rating', label: 'Rating (1-5)', type: 'number', min: 1, max: 5, step: 1 },
          { name: 'comments', label: 'Comments', type: 'textarea' },
          { name: 'feedback', label: 'Feedback', type: 'textarea' }
        ]}
        onSubmit={async (values) => {
          if (!selectedDisposition) {
            return
          }

          const payload: CreateAgentFeedbackPayload = {
            portfolio_home_disposition_id: selectedDisposition.disposition_id,
            showing_date: values.showing_date,
            status: values.status,
            counterparty_type: values.counterparty_type || null,
            interest: values.interest || null,
            overall_experience: values.overall_experience || null,
            price_opinion: values.price_opinion || null,
            rating: values.rating ? Number(values.rating) : null,
            comments: values.comments || null,
            feedback: values.feedback || null,
            agent: getAgentPayload(values)
          }
          await postAndRefresh('agent-feedback', payload, { details: true })
        }}
      />

      <ConfirmModal
        title='Discard unsaved detail edits?'
        body={
          <Text>
            {pendingDetailEditCount} unsaved row{pendingDetailEditCount === 1 ? '' : 's'} will be
            discarded before{' '}
            {pendingDetailsNavigation?.disposition
              ? 'opening another disposition'
              : 'closing details'}
            .
          </Text>
        }
        confirmLabel='Discard edits'
        colorScheme='red'
        isOpen={discardDetailsModal.isOpen}
        onClose={() => {
          discardDetailsModal.onClose()
          setPendingDetailsNavigation(null)
        }}
        onConfirm={async () => {
          setListPriceUpdates({})
          setBidUpdates({})
          setFeedbackUpdates({})
          setSelectedBid(null)
          setSelectedFeedback(null)
          selectDetails(pendingDetailsNavigation?.disposition ?? null)
        }}
      />

      <ConfirmModal
        title='Delete Agent'
        body={
          <Text>{selectedAgent ? selectedAgent.name : 'Selected agent'} will be deactivated.</Text>
        }
        confirmLabel='Delete'
        isOpen={deleteAgentModal.isOpen}
        onClose={deleteAgentModal.onClose}
        onConfirm={async () => {
          if (!selectedAgent) {
            return
          }
          await deleteAndRefresh(`agents/${selectedAgent.id}`)
          setAgentUpdates((current) => {
            const next = { ...current }
            delete next[selectedAgent.id]
            return next
          })
          setSelectedAgent(null)
        }}
      />

      <ConfirmModal
        title='Delete Feedback'
        body={<Text>The selected feedback row will be deactivated.</Text>}
        confirmLabel='Delete'
        isOpen={deleteFeedbackModal.isOpen}
        onClose={deleteFeedbackModal.onClose}
        onConfirm={async () => {
          if (!selectedFeedback) {
            return
          }
          await deleteAndRefresh(`agent-feedback/${selectedFeedback.id}`, { details: true })
          setFeedbackUpdates((current) => {
            const next = { ...current }
            delete next[selectedFeedback.id]
            return next
          })
          setSelectedFeedback(null)
        }}
      />

      <ConfirmModal
        title='Mark Disposition Sold'
        body={
          <Stack spacing={2}>
            <Text>
              The selected bid will become the accepted bid and the home exit fields will be
              updated.
            </Text>
            {selectedBid && (
              <>
                <Divider />
                <Text fontSize='sm'>Buyer: {selectedBid.counterparty_name}</Text>
                <Text fontSize='sm'>Sale Price: {formatMoney(selectedBid.bid_price)}</Text>
                <Text fontSize='sm'>Closing Date: {formatDate(selectedBid.closing_date)}</Text>
              </>
            )}
            {hasPendingDetailEdits && (
              <Alert status='warning'>
                <AlertIcon />
                Save or discard all pending detail edits before marking this bid sold.
              </Alert>
            )}
          </Stack>
        }
        confirmLabel='Mark Sold'
        colorScheme='teal'
        isOpen={markSoldModal.isOpen}
        onClose={markSoldModal.onClose}
        onConfirm={async () => {
          if (!selectedBid) {
            return
          }
          if (selectedBidCannotBeSold) {
            toast({
              title: 'Canceled or terminated bids cannot be marked sold',
              status: 'warning',
              duration: 3500,
              isClosable: true
            })
            throw new Error('Cannot mark a canceled or terminated bid sold')
          }
          if (hasPendingDetailEdits) {
            toast({
              title: 'Save or discard detail edits first',
              status: 'warning',
              duration: 3500,
              isClosable: true
            })
            throw new Error('Cannot mark a bid sold with pending detail edits')
          }
          try {
            await postMutation.mutateAsync({ endpoint: `bids/${selectedBid.id}/closed` })
            if (selectedDisposition)
              discardDispositionStatusEdit(selectedDisposition.disposition_id)
            await invalidateSummary()
            await invalidateDetails()
            if (selectedDisposition) {
              reconcileInactiveDetails(
                selectedDisposition.disposition_id,
                DISPOSITION_STATUSES.SOLD
              )
            }
            toast({ title: 'Disposition marked sold', status: 'success', duration: 2500 })
          } catch (error) {
            showToastError(toast, 'Unable to mark disposition sold', error)
            throw error
          }
        }}
      />
    </DispositionsPageLayout>
  )
}

export default Dispositions
